class MeriphoraCardQuickAdd extends HTMLElement {
  constructor() {
    super();

    this.productData = null;
    this.selectedValues = {};
    this.pendingCartAdd = false;
    this.activeTrigger = null;
    this.activeSubmitButton = null;
    this.activeSheet = null;
    this.sheetPlaceholder = null;
    this.cartDrawerAutoOpenWasEnabled = false;
    this.successCloseTimeout = null;
  }

  connectedCallback() {
    this.productData = this.readInlineProductData();
    this.resetSelection();
    this.updateProductSummary();
    this.updateButtons();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerdown', this.stopSheetPressEvent, { capture: true });
    this.addEventListener('pointerup', this.stopSheetPressEvent, { capture: true });
    this.addEventListener('pointercancel', this.stopSheetPressEvent, { capture: true });
    this.addEventListener('mousedown', this.stopSheetPressEvent, { capture: true });
    this.addEventListener('touchstart', this.stopSheetPressEvent, { capture: true, passive: true });
    this.addEventListener('touchend', this.stopSheetPressEvent, { capture: true });
    this.addEventListener('touchcancel', this.stopSheetPressEvent, { capture: true });
    this.addEventListener('meriphora:quick-add-product-change', this.handleProductChange);
    document.addEventListener('cart:update', this.handleCartUpdate);

    this.querySelectorAll('[data-meriphora-stop-card-hover]').forEach((panel) => {
      panel.addEventListener('pointerenter', this.handlePanelPointerEnter);
      panel.addEventListener('pointerleave', this.handlePanelPointerLeave);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerdown', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('pointerup', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('pointercancel', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('mousedown', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('touchstart', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('touchend', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('touchcancel', this.stopSheetPressEvent, { capture: true });
    this.removeEventListener('meriphora:quick-add-product-change', this.handleProductChange);
    document.removeEventListener('cart:update', this.handleCartUpdate);
    window.clearTimeout(this.successCloseTimeout);
    this.restoreSheetPlacement();

    this.querySelectorAll('[data-meriphora-stop-card-hover]').forEach((panel) => {
      panel.removeEventListener('pointerenter', this.handlePanelPointerEnter);
      panel.removeEventListener('pointerleave', this.handlePanelPointerLeave);
    });
  }

  stopSheetPressEvent = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[data-meriphora-quick-add-sheet]')) return;

    event.stopPropagation();

    if (event.type === 'mousedown' && event.cancelable) {
      event.preventDefault();
    }

    if (event.type === 'touchend' || event.type === 'pointerup') {
      window.getSelection?.()?.removeAllRanges();
    }
  };

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const openButton = target.closest('[data-meriphora-quick-add-open]');
    if (openButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      if (this.visibleOptionGroups.length === 0) {
        this.addVariant(this.findDefaultVariant(), openButton);
        return;
      }

      this.openSheet(openButton);
      return;
    }

    const closeButton = target.closest('[data-meriphora-quick-add-close]');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.closeSheet();
      return;
    }

    const submitButton = target.closest('[data-meriphora-quick-add-submit]');
    if (submitButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();

      const variant = this.findSelectedVariant();
      if (variant) this.addVariant(variant, submitButton);
      return;
    }

    const valueButton = target.closest('[data-meriphora-quick-add-value]');
    if (!(valueButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (valueButton.disabled || valueButton.classList.contains('is-unavailable')) return;

    const isSheetValue = Boolean(valueButton.closest('[data-meriphora-quick-add-sheet]'));
    const isDesktopValue = Boolean(valueButton.closest('[data-meriphora-card-quick-add-desktop]'));

    this.selectOption(valueButton);
    valueButton.blur();
    window.getSelection?.()?.removeAllRanges();

    if (isSheetValue || isDesktopValue) {
      return;
    }

    const variant = this.findSelectedVariant();
    const optionHandle = valueButton.dataset.optionHandle || '';
    const shouldAdd = variant && (optionHandle === 'size' || this.requiredVisibleOptionsSelected());

    if (shouldAdd) {
      this.addVariant(variant, valueButton);
    }
  };

  handleProductChange = (event) => {
    const productId = event.detail?.productId || this.dataset.productId;
    const productData = this.readProductDataTemplate(productId);

    if (!productData) return;

    this.productData = productData;
    this.dataset.productId = String(productData.productId || productId || '');

    if (event.detail?.variantId) {
      this.dataset.defaultVariantId = String(event.detail.variantId);
    } else if (productData.defaultVariantId) {
      this.dataset.defaultVariantId = String(productData.defaultVariantId);
    }

    this.resetSelection();
    this.updateFormVariant(this.findDefaultVariant()?.id || this.dataset.defaultVariantId);
    this.updateProductSummary();
    this.updateButtons();
  };

  handleCartUpdate = (event) => {
    if (!this.pendingCartAdd) return;

    this.pendingCartAdd = false;
    this.restoreCartAutoOpen();

    if (event.detail?.data?.didError) {
      this.setSubmitting(false);
      return;
    }

    this.playSubmitSuccess();
  };

  handlePanelPointerEnter = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;

    const gallery = this.closest('.card-gallery');
    gallery?.dispatchEvent(this.createPointerEvent('pointerleave'));
  };

  handlePanelPointerLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;

    const gallery = this.closest('.card-gallery');
    const nextTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (!gallery?.contains(nextTarget)) return;

    gallery.dispatchEvent(this.createPointerEvent('pointerenter'));
  };

  openSheet(trigger) {
    const sheet = this.getSheet();
    if (!(sheet instanceof HTMLElement)) return;

    this.activeTrigger = trigger;
    this.activeSheet = sheet;
    this.clearSheetSubmitAnimation();
    this.updateProductSummary();
    this.portalSheet(sheet);
    sheet.hidden = false;
    this.setSheetOpenState(true);
    document.documentElement.style.setProperty('overflow', 'hidden');
  }

  closeSheet() {
    const sheet = this.getSheet();
    if (sheet instanceof HTMLElement) sheet.hidden = true;

    this.clearSheetSubmitAnimation();
    this.setSheetOpenState(false);
    this.restoreSheetPlacement();
    document.documentElement.style.removeProperty('overflow');

    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.focus({ preventScroll: true });
    }

    this.activeTrigger = null;
  }

  portalSheet(sheet) {
    if (sheet.parentElement === document.body) return;

    this.sheetPlaceholder = document.createComment('meriphora-card-quick-add-sheet');
    sheet.parentNode?.insertBefore(this.sheetPlaceholder, sheet);
    document.body.appendChild(sheet);

    sheet.addEventListener('click', this.handleClick);
    sheet.addEventListener('pointerdown', this.stopSheetPressEvent, { capture: true });
    sheet.addEventListener('pointerup', this.stopSheetPressEvent, { capture: true });
    sheet.addEventListener('pointercancel', this.stopSheetPressEvent, { capture: true });
    sheet.addEventListener('mousedown', this.stopSheetPressEvent, { capture: true });
    sheet.addEventListener('touchstart', this.stopSheetPressEvent, { capture: true, passive: true });
    sheet.addEventListener('touchend', this.stopSheetPressEvent, { capture: true });
    sheet.addEventListener('touchcancel', this.stopSheetPressEvent, { capture: true });
  }

  restoreSheetPlacement() {
    const sheet = this.getSheet();

    if (sheet instanceof HTMLElement) {
      sheet.removeEventListener('click', this.handleClick);
      sheet.removeEventListener('pointerdown', this.stopSheetPressEvent, { capture: true });
      sheet.removeEventListener('pointerup', this.stopSheetPressEvent, { capture: true });
      sheet.removeEventListener('pointercancel', this.stopSheetPressEvent, { capture: true });
      sheet.removeEventListener('mousedown', this.stopSheetPressEvent, { capture: true });
      sheet.removeEventListener('touchstart', this.stopSheetPressEvent, { capture: true });
      sheet.removeEventListener('touchend', this.stopSheetPressEvent, { capture: true });
      sheet.removeEventListener('touchcancel', this.stopSheetPressEvent, { capture: true });
    }

    if (sheet instanceof HTMLElement && this.sheetPlaceholder?.parentNode) {
      this.sheetPlaceholder.parentNode.insertBefore(sheet, this.sheetPlaceholder);
      this.sheetPlaceholder.remove();
    }

    this.sheetPlaceholder = null;
    this.activeSheet = null;
  }

  getSheet() {
    return this.activeSheet || this.querySelector('[data-meriphora-quick-add-sheet]');
  }

  setSheetOpenState(isOpen) {
    this.toggleAttribute('data-sheet-open', isOpen);

    const quickAdd = this.closest('quick-add-component');
    if (quickAdd instanceof HTMLElement) quickAdd.toggleAttribute('data-sheet-open', isOpen);

    const productCard = this.closest('product-card');
    if (productCard instanceof HTMLElement) productCard.toggleAttribute('data-quick-add-sheet-open', isOpen);
  }

  selectOption(button) {
    const optionIndex = Number(button.dataset.optionIndex);
    const optionHandle = button.dataset.optionHandle || '';
    const optionValue = button.dataset.optionValue || button.textContent?.trim() || '';
    const isDesktopValue = Boolean(button.closest('[data-meriphora-card-quick-add-desktop]'));

    if (!Number.isFinite(optionIndex) || !optionValue) return;

    this.selectedValues[optionIndex] = optionValue;

    if (optionHandle === 'size') {
      this.selectedValues.sizeWasChosen = true;

      if (isDesktopValue) {
        const lengthIndex = this.getOptionIndexByHandle('length');
        if (Number.isFinite(lengthIndex)) {
          delete this.selectedValues[lengthIndex];
          this.selectedValues.lengthWasChosen = false;
        }
      }
    }

    if (optionHandle === 'length') {
      this.selectedValues.lengthWasChosen = true;
    }

    this.updateButtons();
  }

  addVariant(variant, trigger) {
    if (!variant?.id || !variant.available) return;
    if (this.pendingCartAdd) return;

    const submitButton = this.querySelector('.meriphora-card-quick-add__native-submit');
    const visibleButton = trigger instanceof HTMLElement ? trigger : null;

    this.updateFormVariant(variant.id);
    this.pendingCartAdd = true;
    this.activeSubmitButton =
      visibleButton instanceof HTMLButtonElement && visibleButton.matches('[data-meriphora-quick-add-submit]')
        ? visibleButton
        : null;
    this.setSubmitting(true, visibleButton);
    this.suppressCartAutoOpen();

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = false;
      submitButton.click();
      return;
    }

    const form = this.querySelector('form');
    if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    }
  }

  updateFormVariant(variantId) {
    const variantInput = this.querySelector('input[name="id"]');
    const productForm = this.querySelector('product-form-component');

    if (variantInput instanceof HTMLInputElement && variantId) {
      variantInput.value = String(variantId);
      variantInput.disabled = false;
    }

    if (productForm instanceof HTMLElement && this.productData?.productId) {
      productForm.dataset.productId = String(this.productData.productId);
    }
  }

  updateButtons() {
    const options = this.productData?.options || [];
    const buttons = this.getValueButtons();

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue || button.textContent?.trim() || '';
      const option = options[optionIndex];
      const isSelected = this.selectedValues[optionIndex] === optionValue;
      const isUnavailable = !this.hasAvailableVariantFor(optionIndex, optionValue);

      button.hidden = !option || !option.values?.includes(optionValue);
      button.disabled = isUnavailable;
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-unavailable', isUnavailable);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
    });

    this.updateSubmitStates();
  }

  setSubmitting(isSubmitting, activeButton = null) {
    this.querySelectorAll('.meriphora-card-quick-add__value.is-adding').forEach((button) => {
      button.classList.remove('is-adding');
      button.removeAttribute('aria-busy');
    });

    if (isSubmitting && activeButton instanceof HTMLElement) {
      activeButton.classList.add('is-adding');
      activeButton.setAttribute('aria-busy', 'true');
    }

    this.getSubmitButtons().forEach((sheetSubmit) => {
      if (!(sheetSubmit instanceof HTMLButtonElement)) return;

      if (isSubmitting) {
        sheetSubmit.removeAttribute('data-added');
        sheetSubmit.disabled = true;
      }

      const isActiveButton = sheetSubmit === activeButton;
      sheetSubmit.classList.toggle('is-adding', isSubmitting && isActiveButton);
      sheetSubmit.setAttribute('aria-busy', isSubmitting && isActiveButton ? 'true' : 'false');
    });

    if (!isSubmitting) {
      this.updateSubmitStates();
    }
  }

  playSubmitSuccess() {
    this.setSubmitting(false);

    const sheet = this.getSheet();
    const sheetIsOpen = sheet instanceof HTMLElement && !sheet.hidden;
    let successButton =
      this.activeSubmitButton instanceof HTMLButtonElement && !this.activeSubmitButton.hidden
        ? this.activeSubmitButton
        : null;

    if (!successButton && sheetIsOpen) {
      const sheetSubmit = this.getSheet()?.querySelector('[data-meriphora-quick-add-submit]');
      successButton = sheetSubmit instanceof HTMLButtonElement ? sheetSubmit : null;
    }

    if (!successButton) {
      this.activeSubmitButton = null;

      if (sheetIsOpen) {
        this.closeSheet();
      } else {
        this.updateSubmitStates();
      }

      return;
    }

    successButton.setAttribute('data-added', 'true');
    successButton.disabled = true;

    window.clearTimeout(this.successCloseTimeout);
    this.successCloseTimeout = window.setTimeout(() => {
      this.successCloseTimeout = null;

      if (sheetIsOpen) {
        this.closeSheet();
      } else {
        successButton.removeAttribute('data-added');
        this.activeSubmitButton = null;
        this.updateSubmitStates();
      }
    }, 900);
  }

  clearSheetSubmitAnimation() {
    window.clearTimeout(this.successCloseTimeout);
    this.successCloseTimeout = null;

    this.getSubmitButtons().forEach((sheetSubmit) => {
      if (!(sheetSubmit instanceof HTMLButtonElement)) return;

      sheetSubmit.removeAttribute('data-added');
      sheetSubmit.classList.remove('is-adding');
      sheetSubmit.setAttribute('aria-busy', 'false');
    });

    this.activeSubmitButton = null;
    this.updateSubmitStates();
  }

  updateSubmitStates() {
    this.updateSheetSubmitState();
    this.updateDesktopFlowState();
  }

  updateSheetSubmitState() {
    const sheetSubmit = this.getSheet()?.querySelector('[data-meriphora-quick-add-submit]');
    if (!(sheetSubmit instanceof HTMLButtonElement)) return;

    const variant = this.findSelectedVariant();
    sheetSubmit.disabled = !variant || !this.requiredVisibleOptionsSelected();
  }

  updateDesktopFlowState() {
    const desktopPanel = this.querySelector('[data-meriphora-card-quick-add-desktop]');
    if (!(desktopPanel instanceof HTMLElement)) return;

    const lengthGroup = desktopPanel.querySelector('[data-meriphora-quick-add-desktop-length]');
    const desktopSubmit = desktopPanel.querySelector('[data-meriphora-quick-add-desktop-submit]');
    const sizeOption = this.getVisibleOptionByHandle('size');
    const lengthOption = this.getVisibleOptionByHandle('length');
    const sizeSelected =
      !sizeOption || Boolean(this.selectedValues.sizeWasChosen && this.selectedValues[Number(sizeOption.position) - 1]);
    const lengthSelected =
      !lengthOption ||
      Boolean(this.selectedValues.lengthWasChosen && this.selectedValues[Number(lengthOption.position) - 1]);
    const variant = this.findSelectedVariant();
    const isReady = Boolean(sizeSelected && lengthSelected && variant);

    if (lengthGroup instanceof HTMLElement) {
      lengthGroup.hidden = !sizeSelected;
    }

    if (desktopSubmit instanceof HTMLButtonElement) {
      desktopSubmit.hidden = !isReady;
      desktopSubmit.disabled = !isReady;
    }
  }

  updateProductSummary() {
    const sheet = this.getSheet();
    const title = sheet?.querySelector('[data-meriphora-quick-add-summary-title]');
    const color = sheet?.querySelector('[data-meriphora-quick-add-summary-color]');
    const image = sheet?.querySelector('[data-meriphora-quick-add-summary-image]');
    const featuredImage = this.productData?.featuredImage;

    if (title) title.textContent = this.productData?.title || '';
    if (color) color.textContent = this.productData?.colorName || '';

    if (!(image instanceof HTMLImageElement)) return;

    if (featuredImage?.url) {
      image.src = featuredImage.url;
      image.alt = featuredImage.alt || this.productData?.title || '';
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.alt = '';
      image.hidden = true;
    }
  }

  resetSelection() {
    this.selectedValues = {};

    const defaultVariant = this.findDefaultVariant();
    const options = this.productData?.options || [];

    options.forEach((option, index) => {
      if (option.handle === 'length' && defaultVariant?.options?.[index]) {
        this.selectedValues[index] = defaultVariant.options[index];
      }
    });
  }

  requiredVisibleOptionsSelected() {
    return this.visibleOptionGroups.every((option) => {
      const index = Number(option.position) - 1;
      if (option.handle === 'size') return Boolean(this.selectedValues.sizeWasChosen && this.selectedValues[index]);
      return Boolean(this.selectedValues[index]);
    });
  }

  getVisibleOptionByHandle(handle) {
    return this.visibleOptionGroups.find((option) => option.handle === handle) || null;
  }

  getOptionIndexByHandle(handle) {
    const option = this.getVisibleOptionByHandle(handle);
    return option ? Number(option.position) - 1 : NaN;
  }

  getValueButtons() {
    const buttons = new Set(this.querySelectorAll('[data-meriphora-quick-add-value]'));
    this.getSheet()
      ?.querySelectorAll('[data-meriphora-quick-add-value]')
      .forEach((button) => buttons.add(button));

    return buttons;
  }

  getSubmitButtons() {
    const buttons = new Set(this.querySelectorAll('[data-meriphora-quick-add-submit]'));
    this.getSheet()
      ?.querySelectorAll('[data-meriphora-quick-add-submit]')
      .forEach((button) => buttons.add(button));

    return buttons;
  }

  findSelectedVariant() {
    const productData = this.productData;
    if (!productData?.variants?.length) return null;

    return productData.variants.find((variant) => {
      if (!variant.available) return false;

      return (productData.options || []).every((option, index) => {
        const selectedValue = this.selectedValues[index];

        if (option.handle === 'size' || option.handle === 'length') {
          return selectedValue ? variant.options[index] === selectedValue : false;
        }

        const defaultVariant = this.findDefaultVariant();
        const defaultValue = defaultVariant?.options?.[index];
        return defaultValue ? variant.options[index] === defaultValue : true;
      });
    });
  }

  findDefaultVariant() {
    const productData = this.productData;
    if (!productData?.variants?.length) return null;

    const defaultId = String(this.dataset.defaultVariantId || productData.defaultVariantId || '');
    return productData.variants.find((variant) => String(variant.id) === defaultId) || productData.variants[0];
  }

  hasAvailableVariantFor(optionIndex, optionValue) {
    const productData = this.productData;
    if (!productData?.variants?.length || !productData.options?.[optionIndex]) return false;

    return productData.variants.some((variant) => {
      if (!variant.available || variant.options?.[optionIndex] !== optionValue) return false;

      return productData.options.every((option, index) => {
        if (index === optionIndex) return true;

        const selectedValue = this.selectedValues[index];
        if (selectedValue) return variant.options[index] === selectedValue;

        if (option.handle === 'size') return true;
        if (option.handle === 'length') return true;

        const defaultVariant = this.findDefaultVariant();
        const defaultValue = defaultVariant?.options?.[index];
        return defaultValue ? variant.options[index] === defaultValue : true;
      });
    });
  }

  readInlineProductData() {
    const script = this.querySelector('[data-meriphora-card-quick-add-product]');
    return this.parseProductData(script?.textContent || '');
  }

  readProductDataTemplate(productId) {
    if (!productId) return null;

    const template = this.closest('product-card')?.querySelector(
      `template[data-meriphora-card-quick-add-template="${this.cssEscape(productId)}"]`
    );

    if (!(template instanceof HTMLTemplateElement)) return null;

    return this.parseProductData(template.content.textContent || '');
  }

  parseProductData(text) {
    try {
      const data = JSON.parse(text);
      if (!data || !Array.isArray(data.options) || !Array.isArray(data.variants)) return null;
      return data;
    } catch (error) {
      console.warn('Could not parse Meriphora quick-add product data', error);
      return null;
    }
  }

  suppressCartAutoOpen() {
    const drawer = document.querySelector('cart-drawer-component');

    if (drawer instanceof HTMLElement && drawer.hasAttribute('auto-open')) {
      this.cartDrawerAutoOpenWasEnabled = true;
      drawer.removeAttribute('auto-open');
    }
  }

  restoreCartAutoOpen() {
    const drawer = document.querySelector('cart-drawer-component');

    if (drawer instanceof HTMLElement && this.cartDrawerAutoOpenWasEnabled) {
      drawer.setAttribute('auto-open', '');
    }

    this.cartDrawerAutoOpenWasEnabled = false;
  }

  cssEscape(value) {
    if (window.CSS?.escape) return CSS.escape(String(value));
    return String(value).replace(/"/g, '\\"');
  }

  createPointerEvent(type) {
    if (typeof PointerEvent === 'function') {
      return new PointerEvent(type, {
        bubbles: true,
        pointerType: 'mouse',
      });
    }

    return new Event(type, { bubbles: true });
  }

  get visibleOptionGroups() {
    return (this.productData?.options || []).filter((option) => option.handle === 'size' || option.handle === 'length');
  }
}

if (!customElements.get('meriphora-card-quick-add')) {
  customElements.define('meriphora-card-quick-add', MeriphoraCardQuickAdd);
}
